import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCreateInvoice } from '../hooks';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { PageHeader } from '../components/page-header';
import { Upload } from 'lucide-react';
import { isFileTooLarge, MAX_UPLOAD_FILE_SIZE_MB } from '../lib/upload';

const UploadInvoice: React.FC = () => {
  const { t } = useTranslation('invoices');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { user } = useAuth();
  const createInvoice = useCreateInvoice();
  const [invoiceMonth, setInvoiceMonth] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [fileError, setFileError] = useState('');
  const [success, setSuccess] = useState(false);


  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selected = event.target.files[0];
      if (isFileTooLarge(selected)) {
        setFile(null);
        setFileError(t('upload.error.fileTooLarge', { max: MAX_UPLOAD_FILE_SIZE_MB }));
        event.target.value = '';
        return;
      }
      setFileError('');
      setFile(selected);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess(false);

    if (!file) {
      setError(t('upload.error.selectFile'));
      return;
    }

    if (!invoiceMonth) {
      setError(t('upload.error.enterMonth'));
      return;
    }

    try {
      await createInvoice.mutateAsync({
        data: {
          invoiceMonth,
          memberId: user?.id || '',
          notes: notes || undefined,
        },
        file,
      });

      setSuccess(true);
      setFile(null);
      setInvoiceMonth('');
      setNotes('');
      
      // Reset file input
      const fileInput = document.getElementById('file-input') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      setTimeout(() => {
        navigate('/invoices');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload invoice');
    }
  };

  return (
    <div data-testid="upload-invoice-page">
      <PageHeader
        title={t('upload.title')}
        description={t('upload.description')}
        testId="upload-invoice-header"
      />

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Card data-testid="upload-invoice-card">
          <CardHeader>
            <CardTitle>{t('upload.title')}</CardTitle>
            <CardDescription>
              {t('upload.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {(error || fileError) && (
              <Alert variant="destructive" className="mb-4" data-testid="upload-invoice-error-alert">
                <AlertDescription>{error || fileError}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert variant="success" className="mb-4" data-testid="upload-invoice-success-alert">
                <AlertDescription>
                  {t('upload.success')}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="upload-invoice-form">
              <div className="space-y-2">
                <Label htmlFor="invoice-month">{t('upload.invoiceMonth')} *</Label>
                <Input
                  id="invoice-month"
                  type="month"
                  value={invoiceMonth}
                  onChange={(e) => setInvoiceMonth(e.target.value)}
                  required
                  data-testid="upload-invoice-input-month"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">{t('upload.notesOptional')}</Label>
                <Textarea
                  id="notes"
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('upload.notesPlaceholder')}
                  data-testid="upload-invoice-input-notes"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="file-input">{t('upload.invoiceFile')} *</Label>
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="upload-invoice-input-file"
                />
                <label htmlFor="file-input">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    asChild
                    data-testid="upload-invoice-button-choose-file"
                  >
                    <span className="flex items-center justify-center gap-2 cursor-pointer">
                      <Upload className="h-4 w-4" />
                      {file ? file.name : t('upload.chooseFile')}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-almost-black/60">
                  {t('upload.acceptedFormats')}
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/invoices')}
                  className="flex-1"
                  data-testid="upload-invoice-button-cancel"
                >
                  {tCommon('buttons.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={createInvoice.isPending}
                  className="flex-1"
                  data-testid="upload-invoice-button-submit"
                >
                  {createInvoice.isPending ? tCommon('uploading') : t('upload.title')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default UploadInvoice;
